/**
 * kanban/useKanbanDrag.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom hook that encapsulates all HTML5 drag-and-drop state for the board.
 *
 * Responsibilities:
 *  - Track which card is being dragged (draggedTask)
 *  - Track which column is currently being hovered over (dragOverColumn)
 *  - Derive an optimistic column map so the UI reorders cards instantly
 *    while the API call is in-flight
 *  - Call `onStatusChange(taskId, newStatus)` when a card is dropped into a
 *    different column; ignore same-column drops (no unnecessary API calls)
 *
 * Returns a stable object of handlers + derived state.
 * The parent (KanbanBoard) passes these down directly to KanbanColumn / KanbanCard.
 */

import { useState, useCallback, useMemo } from 'react';
import { groupTasksByStatus } from './kanbanUtils';

/**
 * @param {object[]} tasks              - flat task list from the parent
 * @param {string[]} columnKeys         - ordered list of status keys
 * @param {Function} onStatusChange     - (taskId: string, newStatus: string) => void
 */
export default function useKanbanDrag(tasks, columnKeys, onStatusChange) {
    const [draggedTask, setDraggedTask] = useState(null);          // full task object
    const [dragOverColumn, setDragOverColumn] = useState(null);    // status key string

    // Optimistic local columns — while dragging we speculatively move the card
    // so the board feels instant. If the API fails the parent's loadTasks will
    // restore the true state on the next render.
    const [optimisticTasks, setOptimisticTasks] = useState(null);  // null = use real tasks

    const effectiveTasks = optimisticTasks ?? tasks;

    const columns = useMemo(
        () => groupTasksByStatus(effectiveTasks, columnKeys),
        [effectiveTasks, columnKeys]
    );

    // ── Drag event handlers ───────────────────────────────────────────────────

    const handleDragStart = useCallback((e, task) => {
        setDraggedTask(task);
        setOptimisticTasks(null); // reset any previous optimistic state
        // Slim ghost image — browsers render a full card by default which
        // looks heavy; this keeps it subtle.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', task._id); // required for Firefox
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggedTask(null);
        setDragOverColumn(null);
        // Optimistic state stays until the parent re-renders with real data,
        // which keeps the card in its new column visually.
    }, []);

    const handleDragOver = useCallback((e, columnKey) => {
        e.preventDefault(); // required to allow drop
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumn(columnKey);
    }, []);

    const handleDragLeave = useCallback((e) => {
        // Only clear when truly leaving the column, not just crossing a child el.
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverColumn(null);
        }
    }, []);

    const handleDrop = useCallback((e, targetColumnKey) => {
        e.preventDefault();
        setDragOverColumn(null);

        if (!draggedTask) return;
        if (draggedTask.status === targetColumnKey) {
            // Same column — nothing to do
            setDraggedTask(null);
            return;
        }

        // 1. Apply optimistic update immediately
        setOptimisticTasks(
            tasks.map((t) =>
                t._id === draggedTask._id ? { ...t, status: targetColumnKey } : t
            )
        );

        // 2. Persist to the API (parent handles success/error + re-fetch)
        onStatusChange(draggedTask._id, targetColumnKey);

        setDraggedTask(null);
    }, [draggedTask, tasks, onStatusChange]);

    return {
        // State
        draggedTask,
        dragOverColumn,
        columns,

        // Handlers — spread directly onto column / card elements
        handleDragStart,
        handleDragEnd,
        handleDragOver,
        handleDragLeave,
        handleDrop,
    };
}
